
import { defineComponent, ref, computed, toRef, watch, PropType } from 'vue'
import QSelect from 'quasar/src/components/select/QSelect.js';
import { useFormModule } from '@/modules/_shared/forms'

export default defineComponent({
    name: 'editable-select',
    props: {
        modelValue: {
            type: String as () => string | null,
            required: true
        },
        selectOptions: {
            type: Array,
            required: true
        },
        nullable: Boolean,
        nullLabel: {
            type: String,
            default: '- no value -'
        },
        clearable: {
            type: Boolean,
            default: true
        },
        updateCallback: {
            type: Function as PropType<(t: string) => void>
        },
        filterFn: {
            type: Function,
            required: false
        }
    },
    emits: ['update:modelValue'],
    setup (props, { emit }) {
        const { validations: { requiredRule } } = useFormModule()
        const originalValue = toRef(props, 'modelValue')
        const editing = ref(false)
        const inputRef = ref<QSelect>()

        const localNullLable = ref(props.nullLabel)

        const currentValue = computed({
            get: () => props.modelValue,
            set: value => emit('update:modelValue', value)
        })

        const isModelNullable = computed(() => currentValue.value === null)
        const showEditButtons = computed(() => editing.value && props.nullable && (inputRef.value?.validate()))
        const processedOptions = computed(() => {
            if (props.nullable) {
                return [
                    { label: props.filterFn ? localNullLable.value : props.nullLabel, value: null },
                    ...props.selectOptions
                ]
            }
            return props.selectOptions
        })

        const cancelEdit = () => {
            currentValue.value = originalValue.value
            disableEditing()
        }

        const confirmEdit = () => {
            if (inputRef.value?.validate()) {
                inputRef.value.blur()
                emitUpdateEvent()
            }
            disableEditing()
        }

        const enableEditing = () => {
            if (editing.value) {
                return
            }
            currentValue.value = originalValue.value
            editing.value = true
            localNullLable.value = ''
        }
        watch(editing, isEditing => {
            if (inputRef.value && isEditing) {
                inputRef.value.showPopup()
            }
        }, {
            flush: 'post'
        })

        const disableEditing = () => {
            setTimeout(() => {
                editing.value = false
                localNullLable.value = props.nullLabel
            }, 20)
        }

        const emitUpdateEvent = () => emit('update:modelValue', currentValue.value)

        return {
            originalValue,
            currentValue,
            editing,
            enableEditing,
            cancelEdit,
            confirmEdit,
            inputRef,
            requiredRule,
            showEditButtons,
            processedOptions,
            isModelNullable
        }
    }
})
